import { NavLink } from "react-router-dom";
import { House, ClipboardList, Receipt, Archive, Activity, UserRound } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { WS_TOKENS } from "@/lib/workspaceTheme";

/**
 * Fixed bottom navigation for the workspace on mobile/tablet.
 * Role-aware:
 *   • Employees see: Home, Applications, Invoices, Archive
 *   • Admins/Managers see: Home, Applications, Follow-up, Account
 */
export default function WorkspaceBottomNav() {
  const { theme, lang, dir } = useI18n();
  const { user } = useAuth();
  const roleCode = user?.role?.code;
  const isAdmin = user?.role?.is_root || ["general_manager", "deputy_manager", "accountant"].includes(roleCode);

  const items = isAdmin
    ? [
        { key: "home",         to: "/",              icon: House,         label: lang === "ar" ? "الرئيسية"  : "Home" },
        { key: "applications", to: "/applications",  icon: ClipboardList, label: lang === "ar" ? "المعاملات": "Apps" },
        { key: "follow",       to: "/applications?view=follow_up", icon: Activity, label: lang === "ar" ? "المتابعة" : "Follow" },
        { key: "invoices",     to: "/applications?view=invoices",   icon: Receipt,  label: lang === "ar" ? "الفواتير" : "Invoices" },
        { key: "account",      to: "/my-earnings",   icon: UserRound,     label: lang === "ar" ? "حسابي"    : "Account" },
      ]
    : [
        { key: "home",         to: "/",              icon: House,         label: lang === "ar" ? "الرئيسية"  : "Home" },
        { key: "applications", to: "/applications",  icon: ClipboardList, label: lang === "ar" ? "المعاملات": "Apps" },
        { key: "invoices",     to: "/applications?view=invoices", icon: Receipt, label: lang === "ar" ? "الفواتير" : "Invoices" },
        { key: "archive",      to: "/applications?archived=1",    icon: Archive, label: lang === "ar" ? "الأرشيف"  : "Archive" },
        { key: "account",      to: "/my-earnings",   icon: UserRound,     label: lang === "ar" ? "حسابي"    : "Account" },
      ];

  // Match active by pathname + query (see WorkspaceSidebar note).
  const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
  const currentSearch = typeof window !== "undefined" ? window.location.search.replace(/^\?/, "") : "";
  const activeKey = (() => {
    for (const it of items) {
      const [p, q] = it.to.split("?");
      if (p === "/") { if (currentPath === "/") return it.key; continue; }
      if (!currentPath.startsWith(p)) continue;
      if (!q) {
        const cur = new URLSearchParams(currentSearch);
        if (it.key === "applications" && !cur.get("view") && cur.get("archived") !== "1") return it.key;
        if (it.key !== "applications") return it.key;
      } else {
        const want = new URLSearchParams(q);
        const have = new URLSearchParams(currentSearch);
        if (Array.from(want.entries()).every(([k, v]) => have.get(k) === v)) return it.key;
      }
    }
    return null;
  })();

  const css = {
    bar: {
      position: "fixed",
      insetInlineStart: 0, insetInlineEnd: 0,
      bottom: 0,
      background: theme.sidebarBg,
      borderTop: `1px solid ${theme.borderSoft}`,
      zIndex: 100,
      display: "grid",
      gridTemplateColumns: `repeat(${items.length}, 1fr)`,
      paddingBottom: "env(safe-area-inset-bottom, 0px)",
      fontFamily: "'Tajawal','Segoe UI', sans-serif",
    },
    link: ({ isActive }) => ({
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center",
      gap: 4,
      padding: "8px 4px",
      minHeight: WS_TOKENS.bottomNavHeight,
      textDecoration: "none",
      color: isActive ? theme.gold : theme.textMuted,
      fontSize: 11, fontWeight: isActive ? 800 : 500,
      transition: `color ${WS_TOKENS.motion.fast}`,
    }),
  };

  return (
    <nav style={css.bar} data-testid="workspace-bottom-nav" dir={dir} aria-label="bottom-nav">
      {items.map(({ key, to, icon: Icon, label }) => {
        const isActive = key === activeKey;
        return (
          <NavLink
            key={key}
            to={to}
            style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", gap: 4, padding: "8px 4px",
              minHeight: WS_TOKENS.bottomNavHeight,
              textDecoration: "none",
              color: isActive ? theme.gold : theme.textMuted,
              fontSize: 11, fontWeight: isActive ? 800 : 500,
              transition: `color ${WS_TOKENS.motion.fast}`,
            }}
            data-testid={`bottomnav-${key}`}
          >
            <Icon size={22} strokeWidth={isActive ? 2.2 : 1.75} />
            <span>{label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}
